import {useCallback, useMemo, useState} from "react";
import FileHelper from "@utils/file-helper";
import {SelectedFileMeta} from "../models/types/convert-document-ai-modal.types";
import {clearConvertAiModalState, saveConvertAiModalState} from "../helpers/convert-ai-modal-state";
import {MAX_FILES} from "../constants/convert-document-ai-modal";
import {validateIncomingFiles} from "../helpers/ai-source-documents-validation";
import {buildRejectionMessage} from "../helpers/ai-source-documents-messages";

interface SourceDocumentsState {
    files: File[];
    meta: SelectedFileMeta[];
}

interface UseAiSourceDocumentsResult {
    files: File[];
    filesMeta: SelectedFileMeta[];
    totalSize: number;
    error: string | null;
    reachedFileLimit: boolean;
    addFiles: (incoming: File[]) => void;
    removeFile: (index: number) => void;
    clearFiles: () => void;
    setError: (message: string | null) => void;
    hydrateMeta: (meta: SelectedFileMeta[]) => void;
    reset: () => void;
}

const EMPTY_STATE: SourceDocumentsState = {files: [], meta: []};

const persist = (meta: SelectedFileMeta[], isProcessing: boolean) =>
    saveConvertAiModalState({files: meta, isProcessing});

/**
 * Holds the source documents the user picks for AI article conversion.
 *
 * Collects files locally, validates each incoming batch (format, size,
 * duplicates, count and total-size limits) and keeps the picked metadata in
 * sync with the persisted modal state. Does not upload anything — the files
 * are sent later, together, by the conversion step.
 */
export const useAiSourceDocuments = (): UseAiSourceDocumentsResult => {
    const [selection, setSelection] = useState<SourceDocumentsState>(EMPTY_STATE);
    const [error, setError] = useState<string | null>(null);

    const totalSize = useMemo(() => FileHelper.getTotalSize(selection.meta), [selection.meta]);

    const reachedFileLimit = selection.files.length >= MAX_FILES;

    const addFiles = useCallback((incoming: File[]) => {
        if (incoming.length === 0) {
            return;
        }
        setSelection(prev => {
            const result = validateIncomingFiles(incoming, prev.files, prev.meta);
            persist(result.acceptedMeta, false);
            setError(buildRejectionMessage(result.rejected));
            return {files: result.accepted, meta: result.acceptedMeta};
        });
    }, []);

    const removeFile = useCallback((index: number) => {
        setSelection(prev => {
            const next: SourceDocumentsState = {
                files: prev.files.filter((_, i) => i !== index),
                meta: prev.meta.filter((_, i) => i !== index)
            };
            if (next.meta.length === 0) {
                clearConvertAiModalState();
            } else {
                persist(next.meta, false);
            }
            return next;
        });
        setError(null);
    }, []);

    const reset = useCallback(() => {
        setSelection(EMPTY_STATE);
        setError(null);
    }, []);

    const clearFiles = useCallback(() => {
        reset();
        clearConvertAiModalState();
    }, [reset]);

    const hydrateMeta = useCallback((meta: SelectedFileMeta[]) => {
        setSelection(prev => ({...prev, meta}));
    }, []);

    return {
        files: selection.files,
        filesMeta: selection.meta,
        totalSize,
        error,
        reachedFileLimit,
        addFiles,
        removeFile,
        clearFiles,
        setError,
        hydrateMeta,
        reset
    };
};
